import type { ScenarioSpec, WorkplacePolicy } from "./types";
import { phishingOfficeScenario as PHISH } from "./phishing-office.scenario";

/**
 * Workplace policies briefing. Own desktop (video + browser only).
 * Does not reuse the phishing-office desktop config, so that course's
 * mail icons and toast stay untouched.
 * Not on the public library until it is published.
 */
const VIDEO = "/cdn/sim/awareness-videos/workplace-policies/workplace-policies.mp4";

export const DEFAULT_WORKPLACE_POLICIES: WorkplacePolicy[] = [
  {
    id: "secure-internet",
    title: "Secure internet usage",
    summary: "Stay vigilant online to keep company data safe.",
    body:
      `When accessing the internet at work, always verify the authenticity of websites before entering any company or personal information. Be cautious of lookalike login pages that mimic legitimate sites; these are often phishing attempts designed to steal your credentials. If you suspect a site is fraudulent, do not proceed and report it to IT immediately.

Avoid downloading files or software from untrusted sources, as these may contain malware that can compromise our network. If you need specific software for work purposes, consult with IT to ensure it is safe and necessary.

Social media and personal email should be accessed sparingly during work hours and never used to share company information. Be mindful of the content you post online, as it can reflect on the company and may be used by malicious actors to target you or the organization.

When using public Wi-Fi, always connect through a company-approved VPN to encrypt your data and protect it from interception. If you receive an unexpected multi-factor authentication (MFA) prompt, do not approve it and report the incident as it could indicate an unauthorized access attempt.`,
  },
  {
    id: "secure-passwords",
    title: "Secure password usage",
    summary: "Protect your accounts with strong, unique passwords.",
    body:
      `Passwords are a critical line of defense for our systems and data. Always use strong, unique passwords for each account, incorporating a mix of upper and lower case letters, numbers, and symbols. Avoid using easily guessed information like birthdays or common words.

Never share your passwords with anyone, including colleagues or supervisors. If you need to provide access to someone, use the approved methods for granting temporary access, such as password managers or shared credentials systems.

Do not store passwords in plain text, such as in emails, chat messages, or on paper. Utilize a company-approved password manager to store and manage your passwords securely.

Regularly update your passwords and immediately change any that you suspect may have been compromised. Be cautious of any requests for your password, especially if they come via email or phone, as these could be phishing attempts. Report any suspicious requests to IT immediately.`,
  },
  {
    id: "acceptable-use",
    title: "Acceptable use of company devices",
    summary: "Use company devices responsibly and securely.",
    body:
      `Company devices are provided for professional use and should be used in accordance with company policies. Personal use should be limited and should not interfere with work responsibilities or consume excessive network resources.

Do not install unauthorized software or applications on company devices, as these can introduce security vulnerabilities. Always consult with IT before installing any new software.

Avoid connecting personal USB sticks or external drives to company devices unless they have been scanned and approved by IT. These devices can carry malware that could compromise our systems.

Ensure that your device is locked when not in use, even if you are stepping away for a short period. This prevents unauthorized access to company information and systems.

Regularly back up important work data using company-approved methods to prevent data loss. If you notice any unusual activity or suspect your device has been compromised, report it to IT immediately.`,
  },
  {
    id: "report-incidents",
    title: "Reporting security incidents",
    summary: "Promptly report any security incidents to protect company data.",
    body:
      `If you encounter any suspicious activity or security incidents, it is crucial to report them immediately to protect our data and systems. Examples of incidents include receiving a ransom note, noticing unauthorized access to your accounts, or discovering malware on your device.

Do not attempt to investigate or resolve the incident on your own, as this could exacerbate the issue. Instead, contact IT or the designated security team with details of the incident, including the time it occurred and any relevant screenshots or error messages.

If you receive a phishing email, do not click on any links or download attachments. Report the email to IT and delete it from your inbox.

Timely reporting of incidents helps us respond quickly and mitigate potential damage. Remember, even if you are unsure whether something constitutes a security incident, it is better to report it and let the experts assess the situation.`,
  },
];

export const workplacePoliciesScenario: ScenarioSpec = {
  version: 1,
  id: "workplace-policies",
  title: "Your workplace policies — read, understand and apply",
  summary: {
    goal: "Learn what your company's policies on internet use, passwords, company devices and incident reporting require, and how to apply them.",
    youDo: "Switch on the computer, watch the policy film, open the company policy site, read and acknowledge each policy, then answer a short quiz.",
    duration: "8-12 min",
    tags: ["security_policies", "passwords", "unsafe_downloads", "usb_devices", "incident_reporting"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Read the company policies, then apply them",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: PHISH.voiceAttackers,
  scene: PHISH.scene,
  devices: [
    {
      id: "policy_desktop",
      kind: "desktop",
      config: {
        clock: "9:05 AM",
        date: "07/10/2026",
        videoBriefing: {
          src: VIDEO,
          title: "Your workplace policies",
        },
        icons: [
          { id: "video", label: "Video", icon: "video", opens: "video" },
          { id: "browser", label: "Browser", icon: "chrome", opens: "browser" },
        ],
        taskbar: [
          { id: "tb_video", label: "Video", icon: "video", opens: "video" },
          { id: "tb_browser", label: "Browser", icon: "chrome", opens: "browser" },
        ],
      },
    },
    {
      id: "policy_browser",
      kind: "policypage",
      config: {
        companyName: "Company policies",
        siteUrl: "policies.company.internal",
        intro: "These are your company's policies. Open each one, read it, and acknowledge it.",
        policies: DEFAULT_WORKPLACE_POLICIES,
      },
    },
  ],
  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Your workplace policies",
      kicker: "Reception",
      guide: { title: "Start", body: "You sit down at your office computer at the start of the day. Your company has published its workplace policies, and a short briefing film is waiting on the desktop." },
    },
    { id: "b_to_monitor", type: "camera", waypointId: "wp_monitor" },
    {
      id: "b_desktop_video",
      type: "device",
      deviceId: "policy_desktop",
      guide: {
        title: "Watch the briefing",
        body: "Click Video to play the workplace policies film on this computer.",
        actionLabel: "Video",
      },
      completeOn: { action: "open_app", arg: "video_done" },
    },
    {
      id: "b_desktop_browser",
      type: "device",
      deviceId: "policy_desktop",
      guide: {
        title: "Open the policies",
        body: "The film is done. Click Browser to open the company policy site.",
        actionLabel: "Browser",
      },
      completeOn: { action: "open_app", arg: "browser" },
    },
    {
      id: "b_policies",
      type: "device",
      deviceId: "policy_browser",
      guide: {
        title: "Read and acknowledge",
        body: "Open each company policy, read it, and acknowledge it. Then click Continue.",
        actionLabel: "Continue",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach",
      type: "teach",
      teach: {
        outcome: "safe",
        body: "Workplace policies are written by your company. The film introduces them. The policy site is where you read the actual rules and confirm you will apply them.",
        spot: [
          "A film does not replace the policy. The document is the rule.",
          "If a habit conflicts with a policy, follow the policy and ask when something is unclear.",
        ],
        protect: [
          "Read the policy your company published, not a copy from somewhere else.",
          "Acknowledge only after you have read it.",
        ],
        remember: "Know the policy, then apply it.",
      },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_source",
          title: "Question 1",
          prompt: "Where do the workplace policies you must follow come from?",
          options: [
            { id: "a", label: "Any website that looks official", icon: "x" },
            { id: "b", label: "Your company. Read the policies it publishes.", correct: true, icon: "check" },
            { id: "c", label: "Whatever a colleague remembers from last year", icon: "alert" },
          ],
          rationale: "The company issues the policies. The film and a colleague's memory do not replace the published document.",
        },
        {
          id: "q_after",
          title: "Question 2",
          prompt: "The policy film has finished. What do you do next?",
          options: [
            { id: "a", label: "Close the computer. The film was enough.", icon: "x" },
            { id: "b", label: "Open the company policy site, read each policy, and acknowledge it.", correct: true, icon: "check" },
            { id: "c", label: "Email the film to yourself and skip the documents.", icon: "alert" },
          ],
          rationale: "The film introduces the topic. You still read and acknowledge the company's own policies.",
        },
        {
          id: "q_apply",
          title: "Question 3",
          prompt: "A policy says not to send company files through personal email. You are in a hurry. What do you do?",
          options: [
            { id: "a", label: "Send it from personal email and mention it later.", icon: "x" },
            { id: "b", label: "Use the method the policy allows, or wait until you can.", correct: true, icon: "check" },
            { id: "c", label: "Send it if the file does not look sensitive.", icon: "alert" },
          ],
          rationale: "Apply the policy as written. Hurry and a guess about sensitivity do not replace it.",
        },
      ],
    },
  ],
  edges: [
    { from: "b_intro", to: "b_to_monitor" },
    { from: "b_to_monitor", to: "b_desktop_video" },
    { from: "b_desktop_video", to: "b_desktop_browser" },
    { from: "b_desktop_browser", to: "b_policies" },
    { from: "b_policies", to: "b_teach" },
    { from: "b_teach", to: "b_quiz" },
  ],
  initialBeatId: "b_intro",
};
